'use client';

import '@chewy/kib-carousels-styles/dist/styles/kib-carousel.css';
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css';
import '@chewy/kib-layout-styles/dist/styles/kib-container.css';
import '../Nav/styles.global.scss';

import { KibButtonNew } from '@chewy/kib-controls-react';
import { KibContainer } from '@chewy/kib-layout-react';
import { KibChewyLogo, KibGetRealHorizontalLogo } from '@chewy/kib-logos-react';
import { TrackedElement } from '@chewy/use-analytics';
import cx from 'classnames';
import Link from 'next/link';

import { PageType } from '../../constants';
import { ANALYTICS_TAG } from '../Analytics/AnalyticsProvider';
import { CHAT_URL } from '../ChatNow/constants';
import { FAQ } from '../FAQ/Faq';
import { FAQS_LANDING } from '../FAQ/faq-data';
import { FadeUpOnScroll } from '../FadeUpOnScroll/FadeUpOnScroll';
import { GoToFormButton } from '../GoToFormButton/GoToFormButton';
import { InfluencerVideos } from '../InfluencerVideos/InfluencerVideos';
import InternalFooter from '../InternalFooter/InternalFooter';
import { FreshPlanRecommendationsQuery } from '../MealPlanFormSteps/__generated__/chewy-api.plan-recommendations';
import { PlaPreChosenPlan } from '../PlaPreChosenPlan/PlaPreChosenPlan';
import { PlaFlavorType, PlaFrequency } from '../PlaPreChosenPlan/utils';
import { Reviews } from '../Reviews/Reviews';
import { Testimonial } from '../Testimonial/Testimonial';
import { Testimonials } from '../Testimonials/Testimonials';

import { LandingHeroVideo } from './LandingHeroVideo';
import { CheckBadgeIcon, GetRealBadgeIcon } from './SecondaryBadgeIcons';
import styles from './styles.module.scss';

import dogBowlBurst from '@/assets/dog-bowl-with-burst-background.png';
import dogInBox from '@/assets/dog-entering-get-real-delivery-box.png';
import dogRunningBurst from '@/assets/dog-running-forward.png';
import flavorBeef from '@/assets/get-real-beef-and-sweet-potato-recipe-packaging.png';
import flavorChicken from '@/assets/get-real-chicken-and-brussels-sprouts-recipe-packaging.png';
import flavorTurkey from '@/assets/get-real-turkey-and-cranberry-recipe-packaging.png';
import handHoldingBurst from '@/assets/hand-holding-get-real-package.png';
import arrivesColdIcon from '@/assets/icon-arrives-cold.svg';
import calendarIcon from '@/assets/icon-calendar.svg';
import expertsIcon from '@/assets/icon-experts.svg';
import freeShippingIcon from '@/assets/icon-free-shipping.svg';
import frozenIcon from '@/assets/icon-frozen.svg';
import humanGradeIcon from '@/assets/icon-human-grade.svg';
import supportIcon from '@/assets/icon-support.svg';
import tenIngredientsIcon from '@/assets/icon-ten-ingredients.svg';
import { useChat } from '@/hooks/use-chat';
import { FreshPromotionFragment } from '@/integrations/chewy-api/__generated__/chewy-api.fresh-products';
import { getEligiblePromotion } from '@/integrations/chewy-api/utils';

const HeroCtaContent = ({ className }: { className?: string }) => (
  <>
    <GoToFormButton pageType={PageType.LANDING} className={className} />

    <div className={styles.secondaryBadges}>
      <span className={styles.secondaryBadge}>
        <GetRealBadgeIcon className={styles.secondaryBadgeIcon} />
        Try a Sampler Box
      </span>
      <span className={styles.secondaryBadge}>
        <CheckBadgeIcon className={styles.secondaryBadgeIcon} />
        Risk-free trial
      </span>
    </div>
  </>
);

export const LandingPage = ({
  promotions,
  plaPlan,
  plaFlavorType,
  plaFrequency,
  isMobileApp = false,
}: {
  promotions?: FreshPromotionFragment[];
  plaPlan?: FreshPlanRecommendationsQuery;
  plaFlavorType?: PlaFlavorType;
  plaFrequency?: PlaFrequency;
  isMobileApp?: boolean;
}) => {
  const { handleChatClick } = useChat(PageType.LANDING);

  const eligiblePromotion = getEligiblePromotion(promotions);

  return (
    <>
      <div
        className={cx(styles.heroSection, {
          [styles.withPla]: !!plaPlan,
          [styles.inNativeMobileApp]: isMobileApp,
        })}
      >
        <TrackedElement
          listeners={{
            onVisibleOnce: {
              event: ANALYTICS_TAG.NAME_MODULE_VIEWED,
            },
          }}
          properties={{
            eventCategory: ANALYTICS_TAG.CATEGORY_LANDING,
            eventAction: ANALYTICS_TAG.EVENT_IMPRESSION,
            eventLabel: 'get-real-landing-page-module',
          }}
        >
          <section className={styles.hero} aria-label="Get Real">
            <LandingHeroVideo />

            <div className={styles.heroForeground}>
              <div className={styles.heroLogo}>
                <KibGetRealHorizontalLogo className={styles.getRealLogo} />

                <div className={styles.heroLogoByline}>
                  <span className={styles.heroLogoBy}>by</span>
                  <KibChewyLogo className={styles.heroChewyLogo} aria-hidden="true" />
                </div>
                <span className={styles.textLogo}>Get Real by Chewy</span>
              </div>

              <div className={styles.heroContent}>
                <div className={styles.heroContentMain}>
                  {eligiblePromotion && (
                    <p className={styles.promoBadge}>
                      {eligiblePromotion.promotion.shortDescription}
                    </p>
                  )}

                  <h1 className={styles.heroHeadline}>
                    Fresh dog food with 10 ingredients or less
                  </h1>
                  <p className={styles.heroSubheadline}>Plus vitamins &amp; minerals</p>

                  {!plaPlan && (
                    <div className={cx(styles.heroCta, styles.heroCtaInHero)}>
                      <HeroCtaContent className={styles.heroCtaButton} />
                    </div>
                  )}
                </div>

                {plaPlan && plaFlavorType && (
                  <FadeUpOnScroll className={styles.plaPreChosenPlanAnimate}>
                    <div className={styles.plaPreChosenPlanWrapper}>
                      <PlaPreChosenPlan
                        plaPlan={plaPlan}
                        type={plaFlavorType}
                        frequency={plaFrequency}
                      />
                    </div>
                  </FadeUpOnScroll>
                )}
              </div>
            </div>
          </section>
        </TrackedElement>
      </div>

      {!plaPlan && (
        <div className={cx(styles.heroCta, styles.heroCtaFixed)}>
          <HeroCtaContent className={styles.heroCtaButton} />
        </div>
      )}

      <div className={cx(styles.landingContent, { [styles.withPla]: !!plaPlan })}>
        <div className={styles.thawAndServeSection}>
          <div className={styles.content}>
            <h2 className={styles.heading}>Just thaw &amp; serve</h2>
            <p className={styles.text}>
              <strong>Get Real</strong> has human-grade ingredients, tasty variety, and custom meal
              plans.
            </p>
          </div>
        </div>

        {/* hide testimonial on large screens if PLA plan is showing */}
        <div className={styles.testimonialWrapper}>
          <Testimonial
            className={cx(styles.testimonial, styles.testimonialHero)}
            title="My dog LOVES this"
            text="He used to pick at his kibble throughout the day, now he gobbles it up as soon as the bowl is put down."
            author="ALLISON"
          />
        </div>

        <div className={cx(styles.parallaxPackaging, styles.parallaxPackaging1)}></div>

        <div className={styles.ingredientsWrapper}>
          <h2 className={styles.heading}>Simple Ingredients</h2>

          <div className={styles.ingredientsSections}>
            <FadeUpOnScroll className={cx(styles.ingredientsSection, styles.humanGrade)}>
              <img alt="" aria-hidden="true" data-a11y="decorative" src={humanGradeIcon.src}></img>
              <h3>Human-grade recipes</h3>
              <p>Gently cooked, with real meat, fruit & veggies.</p>
            </FadeUpOnScroll>

            <FadeUpOnScroll className={cx(styles.ingredientsSection, styles.tenIngredients)}>
              <img
                alt=""
                aria-hidden="true"
                data-a11y="decorative"
                src={tenIngredientsIcon.src}
              ></img>
              <h3>10 ingredients or less, plus vitamins & minerals</h3>
              <p>No weird additives, no fillers, no nonsense.</p>
            </FadeUpOnScroll>

            <FadeUpOnScroll className={cx(styles.ingredientsSection, styles.experts)}>
              <img alt="" aria-hidden="true" data-a11y="decorative" src={expertsIcon.src}></img>
              <h3>Formulated by experts</h3>
              <p>
                Including certified pet nutritionists and doctors of animal nutrition & food
                science.
              </p>
            </FadeUpOnScroll>

            <FadeUpOnScroll className={cx(styles.ingredientsSection, styles.frozen)}>
              <img alt="" aria-hidden="true" data-a11y="decorative" src={frozenIcon.src}></img>
              <h3>Frozen keeps it fresh</h3>
              <p>Frozen to lock in nutrients and freshness.</p>
            </FadeUpOnScroll>
          </div>
        </div>

        <div className={styles.recipesWrapper}>
          <KibContainer className={styles.recipes}>
            <h2 className={styles.heading}>Mix & Match Recipes</h2>

            <p className={styles.description}>
              <strong>Get Real</strong> was carefully crafted so you can easily switch up the menu
              to keep meals exciting—without upset tummies.
            </p>

            <div className={styles.recipesSections}>
              <FadeUpOnScroll className={styles.recipesSection}>
                <img src={flavorChicken.src} alt="" />
                <h3>Chicken & Brussels Sprouts recipe</h3>
              </FadeUpOnScroll>

              <FadeUpOnScroll className={styles.recipesSection}>
                <img src={flavorBeef.src} alt="" />
                <h3>Beef & Sweet Potato recipe</h3>
              </FadeUpOnScroll>

              <FadeUpOnScroll className={styles.recipesSection}>
                <img src={flavorTurkey.src} alt="" />
                <h3>Turkey & Cranberry recipe</h3>
              </FadeUpOnScroll>
            </div>

            <Link href="/get-real/food-info" passHref legacyBehavior>
              <KibButtonNew emphasis="tertiary" theme="action" className={styles.tertiaryButton}>
                Explore ingredients
              </KibButtonNew>
            </Link>
          </KibContainer>
        </div>

        <div className={styles.planInfoWrapper}>
          <KibContainer className={styles.planInfo}>
            <h2 className={styles.heading}>Healthy Made Simple</h2>

            <div className={styles.planInfoContents}>
              <FadeUpOnScroll className={styles.planInfoContent}>
                <img src={handHoldingBurst.src} alt="" />

                <div className={styles.planInfoText}>
                  <h3>Simple to serve</h3>
                  <p>Pre-portioned packs mean less mess, less hassle.</p>
                </div>
              </FadeUpOnScroll>

              <FadeUpOnScroll className={cx(styles.planInfoContent, styles.dogBowlBurstContent)}>
                <img src={dogBowlBurst.src} alt="" />

                <div className={styles.planInfoText}>
                  <h3>Full meals or mixers</h3>
                  <p>Serve exclusively or mix it in for extra nutrition. </p>
                </div>
              </FadeUpOnScroll>

              <FadeUpOnScroll className={styles.planInfoContent}>
                <img src={dogRunningBurst.src} alt="" />

                <div className={styles.planInfoText}>
                  <h3>Custom plans</h3>
                  <p>We&rsquo;ll tailor your plan to fit your dog&rsquo;s needs. </p>
                </div>
              </FadeUpOnScroll>
            </div>

            <Link href="/get-real/how-it-works" passHref legacyBehavior>
              <KibButtonNew className={styles.secondaryButton} emphasis="secondary" theme="action">
                How it works
              </KibButtonNew>
            </Link>
          </KibContainer>
        </div>

        <div className={cx(styles.parallaxPackaging, styles.parallaxPackaging2)}></div>

        <div className={styles.deliveryOuterWrapper}>
          <div className={styles.testimonialWrapper}>
            <Testimonial
              className={styles.testimonial}
              title="She Loves It!"
              text="She dances in circles when it's mealtime, and she has more energy and is more active!"
              author="Ladd"
            />
          </div>

          <div className={styles.dogInBoxWrapper}>
            <div className={styles.contentWrapper}>
              <div className={styles.dogInBox}>
                <img src={dogInBox.src} alt="" />
              </div>
            </div>
          </div>

          <div className={styles.deliveryWrapper}>
            <KibContainer className={styles.delivery}>
              <h2 className={styles.heading}>Fresh Food to your doggy door</h2>

              <div className={styles.deliverySections}>
                <FadeUpOnScroll className={styles.deliverySection}>
                  <img alt="" aria-hidden="true" data-a11y="decorative" src={calendarIcon.src} />
                  <h3>Delivered on your schedule</h3>
                  <p>You choose the frequency that works for you&mdash;and your freezer.</p>
                </FadeUpOnScroll>

                <FadeUpOnScroll className={styles.deliverySection}>
                  <img
                    alt=""
                    aria-hidden="true"
                    data-a11y="decorative"
                    src={freeShippingIcon.src}
                  />
                  <h3>Fast, free shipping always</h3>
                  <p>Plus, easy tracking and notifications keep you in the know.</p>
                </FadeUpOnScroll>

                <FadeUpOnScroll className={styles.deliverySection}>
                  <img alt="" aria-hidden="true" data-a11y="decorative" src={arrivesColdIcon.src} />
                  <h3>Arrives cold, for freshness</h3>
                  <p>Pop food in the fridge and freezer. Recycle all the shipping packaging. </p>
                </FadeUpOnScroll>
              </div>

              <Link href="/get-real/how-it-works" passHref legacyBehavior>
                <KibButtonNew emphasis="secondary" theme="action">
                  How it works
                </KibButtonNew>
              </Link>
            </KibContainer>
          </div>
        </div>

        <div className={styles.testimonialsWrapper}>
          <KibContainer>
            <h2 className={styles.heading}>Real dog people love the results</h2>

            <Testimonials />
          </KibContainer>
        </div>

        <div className={styles.videosWrapper}>
          <KibContainer>
            <h2 className={styles.heading}>What Dogs Are Saying</h2>
            <h3 className={styles.subHeading}>#ChewyPartner</h3>

            <InfluencerVideos />
          </KibContainer>
        </div>

        <div className={styles.reviewsWrapper}>
          <KibContainer className={styles.reviews}>
            <h2 className={styles.heading}>Customer Reviews</h2>

            <Reviews />
          </KibContainer>
        </div>

        <div className={styles.supportWrapper}>
          <KibContainer className={styles.support}>
            <img
              className={styles.image}
              src={supportIcon.src}
              alt=""
              aria-hidden="true"
              data-a11y="decorative"
            />
            <h2 className={styles.heading}>24/7 CHEWY SUPPORT</h2>

            <p>
              Our team is here to help with any questions, from picking a plan to food transitions.
              We&rsquo;ll talk you through it all.
            </p>

            <KibButtonNew
              emphasis="tertiary"
              theme="action"
              onClick={handleChatClick}
              href={CHAT_URL}
              target={'_self'}
              className={styles.tertiaryButton}
            >
              Contact
            </KibButtonNew>
          </KibContainer>
        </div>

        <div className={styles.faqWrapper}>
          <KibContainer className={styles.faq}>
            <h2 className={styles.heading}>FAQs</h2>

            <FAQ faqs={FAQS_LANDING} />
          </KibContainer>
        </div>

        <InternalFooter pageType={PageType.LANDING} />
      </div>
    </>
  );
};
